import { combineReducers, configureStore, Reducer } from "@reduxjs/toolkit";
import { useDispatch } from "react-redux";
import { createEpicMiddleware } from "redux-observable";
import { createMigrate, persistReducer, persistStore } from "redux-persist";
import { BehaviorSubject } from "rxjs";
import { switchMap } from "rxjs/operators";
import { UserTracker } from "user-tracker";
import { SetLocalStorageItem } from "utils/hooks/use-local-storage-store";
import { ActivitiesSliceReducer } from "./modules/activities/activities.slice";
import { AppStateSliceReducers } from "./modules/app-state/app-state.slice";

import { ContactListsSliceReducer } from "./modules/contact-lists/contact-lists.slice";
import { ContactsSliceReducer } from "./modules/contacts/contacts.slice";

import { GroupsSliceReducer } from "./modules/groups/groups.slice";
import { BookingsSliceReducer } from "./modules/bookings/bookings.slice";
import { BookingActivityLogsSliceReducer } from "./modules/booking-activity-logs/booking-activity-logs.slice";
import { BookingDiscussionItemsSliceReducer } from "./modules/booking-discussion-items/booking-discussion-items.slice";
import { CustomerInvoicesSliceReducer } from "./modules/customer-invoices/customer-invoices.slice";
import { CustomerPaymentReceiptsSliceReducer } from "./modules/customer-payment-receipts/customer-payment-receipts.slice";
import { CustomerCreditVouchersSliceReducer } from "./modules/customer-credit-vouchers/customer-credit-vouchers.slice";
import { ProfitAllocationRecordsSliceReducer } from "./modules/profit-allocation-records/profit-allocation-records.slice";
import { SupplierBillsSliceReducer } from "./modules/supplier-bills/supplier-bills.slice";
import { SupplierBillPaymentReceiptsSliceReducer } from "./modules/supplier-bill-payment-receipts/supplier-bill-payment-receipts.slice";
import { HolidayQuotesSliceReducer } from "./modules/holiday-quotes/holiday-quotes.slice";

import { NotificationsSliceReducer } from "./modules/notifications/notifications.slice";
import { OpportunitiesSliceReducer } from "./modules/opportunities/opportunities.slice";

import { PreferenceSliceReducers } from "./modules/preferences/preferences.store";

import { RemoteResourcesSliceReducers } from "./modules/remote-resource-cache/remote-resource-cache.store";

import { ScheduledReportsSliceReducer } from "./modules/scheduled-reports/scheduled-reports.slice";

import { TasksSliceReducer } from "./modules/tasks/tasks.slice";

import {
  UIStateSliceReducers,
  uiStore_initialState,
} from "./modules/ui-state/ui-state.slice";
import { UserPresenceSliceReducers } from "./modules/user-presence/user-presence.slice";
import { UsersSliceReducers } from "./modules/users/users.store";
import { OrganizationsSliceReducers } from "./modules/workspace/workspace.store";
import { storage } from "./storage.browser";
import combinedEpics from "./store.epics";
import { storeMigrations } from "./store.migrations";
import { iStore } from "./store.model";
import { RegisterWatchers } from "./store.watchers";
import { HolidayQuotesXSliceReducer } from "./modules/holiday-quotes-x/holiday-quotes.slice";
import { SuppliersSliceReducer } from "./modules/suppliers/suppliers.slice";
import { CustomerPaymentMethodsSliceReducer } from "./modules/customer-payment-methods/customer-payment-methods.slice";
import { SupplierPaymentMethodsSliceReducer } from "./modules/supplier-payment-methods/supplier-payment-methods.slice";
import { LedgersSliceReducer } from "./modules/ledgers/ledgers.slice";
import { LedgerTransactionsSliceReducer } from "./modules/ledger-transactions/ledger-transactions.slice";
import { BrandsSliceReducer } from "./modules/brands/brands.slice";

// Epics
export const epicMiddleware = createEpicMiddleware();

// If Local Storage is null, fill it with empty object
// Fix for (autoRehydrate.js:54)
const persistKey = "root";
(() => {
  if (!localStorage.getItem(`persist:${persistKey}`)) {
    SetLocalStorageItem(`persist:${persistKey}`, "{}");
  }
})();

const rootPersistConfig = {
  key: persistKey,
  storage,
  // Note: Does not accept dot notation
  // blacklist: [
  //   "contacts",
  // ],
  version: 4,
  whitelist: ["users", "uiState", "preferences", "appState"],
  // stateReconciler: autoMergeLevel2 as any,
  migrate: createMigrate(storeMigrations as any, { debug: false }),
  transforms: [],
};

const rootReducer: Reducer<iStore> = combineReducers({
  appState: AppStateSliceReducers,
  uiState: UIStateSliceReducers,
  preferences: PreferenceSliceReducers,
  notifications: NotificationsSliceReducer,
  organizations: OrganizationsSliceReducers,
  users: UsersSliceReducers,
  userPresence: UserPresenceSliceReducers,
  userGroups: GroupsSliceReducer,
  contacts: ContactsSliceReducer,
  contactLists: ContactListsSliceReducer,
  activities: ActivitiesSliceReducer,
  opportunities: OpportunitiesSliceReducer,
  scheduledReports: ScheduledReportsSliceReducer,
  tasks: TasksSliceReducer,
  // Local Knowledge Base

  holidayQuotes: HolidayQuotesSliceReducer,
  holidayQuotesX: HolidayQuotesXSliceReducer,
  suppliers: SuppliersSliceReducer,
  customerPaymentMethods: CustomerPaymentMethodsSliceReducer,
  supplierPaymentMethods: SupplierPaymentMethodsSliceReducer,
  ledgers: LedgersSliceReducer,
  ledgerTransactions: LedgerTransactionsSliceReducer,
  brands: BrandsSliceReducer,
  bookings: BookingsSliceReducer,
  bookingActivityLogs: BookingActivityLogsSliceReducer,
  bookingDiscussionItems: BookingDiscussionItemsSliceReducer,
  customerInvoices: CustomerInvoicesSliceReducer,
  customerPaymentReceipts: CustomerPaymentReceiptsSliceReducer,
  customerCreditVouchers: CustomerCreditVouchersSliceReducer,
  profitAllocationRecords: ProfitAllocationRecordsSliceReducer,
  supplierBills: SupplierBillsSliceReducer,
  supplierBillPaymentReceipts: SupplierBillPaymentReceiptsSliceReducer,

  remoteResources: RemoteResourcesSliceReducers,
});

const rootPersistedReducer = persistReducer(rootPersistConfig, rootReducer);

const rootPersistedReducerWithReset = (state: any, action: any) => {
  if (action.type === "users/logout") {
    const { router, _persist, preferences, appState, uiState, hq } = state;
    state = {
      router,
      _persist,
      preferences,
      appState,
      hq,
      uiState: {
        ...uiStore_initialState,
        theme: { isDarkMode: uiState?.theme?.isDarkMode },
      },
    };
    UserTracker.reset();
  }
  return rootPersistedReducer(state, action);
};

export const store = configureStore({
  reducer: rootPersistedReducerWithReset,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(epicMiddleware),
  devTools: process.env.NODE_ENV !== "production",
});

(window as any).reduxStore = store;

const epic$ = new BehaviorSubject(combinedEpics);
// Every time a new epic is given to epic$ it
// will unsubscribe from the previous one then
// call and subscribe to the new one because of
// how switchMap works
const hotReloadingEpic = (...args: any) =>
  epic$.pipe(switchMap((epic) => (epic as any)(...args)));

epicMiddleware.run(hotReloadingEpic as any);

// if ((module as any).hot) {
//   (module as any).hot.accept("./store.epics", () => {
//     const nextRootEpic = require("./store.epics").default;
//     epic$.next(nextRootEpic);
//   });
// }

export const persistor = persistStore(store);

// (async () => {
//   await persistor.purge();
// })();

export type AppDispatch = typeof store.dispatch;
export const useAppDispatch = () => useDispatch<AppDispatch>();

RegisterWatchers(store);
